import React, { useMemo } from 'react';
import styles from './LineChart.module.css';

interface DataPoint {
  label: string;
  value: number;
}

interface LineChartProps {
  data: DataPoint[];
  height?: number;
  color?: string;
}

export const LineChart: React.FC<LineChartProps> = ({
  data,
  height = 200,
  color = '#3B82F6'
}) => {
  const { points, areaPath, linePath, yLabels, maxValue, isEmpty } = useMemo(() => {
    const chartHeight = 100;
    const chartWidth = 100;

    // Guard: an empty dataset (e.g. backend returned no revenue_trend) used
    // to crash on `pts[pts.length - 1].x`. Render an empty chart instead.
    if (!data || data.length === 0) {
      return {
        points: [] as Array<{ x: number; y: number; label: string; value: number }>,
        areaPath: '',
        linePath: '',
        yLabels: ['$0', '$0', '$0', '$0', '$0'],
        maxValue: 0,
        isEmpty: true,
      };
    }

    const max = Math.max(...data.map(d => d.value));
    const min = 0;
    // Avoid divide-by-zero when every value is 0 or there is only one point.
    const range = max - min || 1;
    const denom = data.length > 1 ? data.length - 1 : 1;

    const pts = data.map((d, i) => ({
      x: (i / denom) * chartWidth,
      y: chartHeight - ((d.value - min) / range) * chartHeight,
      label: d.label,
      value: d.value,
    }));

    const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    const area = pts.length > 0
      ? `${line} L ${pts[pts.length - 1].x} ${chartHeight} L ${pts[0].x} ${chartHeight} Z`
      : '';

    const labels = [0, max * 0.25, max * 0.5, max * 0.75, max].map(v =>
      v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`
    );

    return {
      points: pts,
      areaPath: area,
      linePath: line,
      yLabels: labels.reverse(),
      maxValue: max,
      isEmpty: false,
    };
  }, [data]);

  return (
    <div className={styles.container} style={{ height }}>
      <div className={styles.yAxis}>
        {yLabels.map((label, i) => (
          <span key={i} className={styles.yLabel}>{label}</span>
        ))}
      </div>
      <div className={styles.chartArea}>
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className={styles.svg}
        >
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map(y => (
            <line
              key={y}
              x1="0"
              y1={y}
              x2="100"
              y2={y}
              stroke="#E5E7EB"
              strokeWidth="0.5"
              vectorEffect="non-scaling-stroke"
            />
          ))}

          {/* Area fill */}
          {areaPath && (
            <path
              d={areaPath}
              fill={`${color}15`}
            />
          )}

          {/* Line */}
          {linePath && (
            <path
              d={linePath}
              fill="none"
              stroke={color}
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}

          {/* Data points */}
          {points.map((p, i) => (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r="3"
              fill="white"
              stroke={color}
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </svg>
        <div className={styles.xAxis}>
          {data.map((d, i) => (
            <span key={i} className={styles.xLabel}>{d.label}</span>
          ))}
        </div>
      </div>
    </div>
  );
};
